import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useOutletContext } from 'react-router-dom';
import { toast } from 'react-toastify';
import { logError } from '../../../../utils/logger';
import {
  ControlledComboBox,
  ControlledSelect,
  ControlledTextField,
  InlineRangeGroup,
  SectionTitle,
} from '../../../shared/components';
import {
  INPUT_MODES,
  INPUT_TYPES,
} from '../../../shared/components/form/TextField/textField.constants';
import {
  FIELD_ORIENTATION,
  LABEL_VARIANT,
} from '../../../shared/components/layout/FormFieldWrapper';
import { validateGreaterThan } from '../../../shared/utils/form-validations';

import { Department, departmentsApi, LookupRow, lookupApis } from 'modules/admin/admin.api';
import { GENDER_PREFERENCES, POSITION_COUNTS } from 'modules/hiring/shared/constants';
import { useAsyncData } from 'modules/shared/hooks/useAsyncData';
import { HIRING_FORM_MODE } from '../hiringRequest.constant';
import { postHiringRequest, updateHiringRequest } from '../hiringRequest.service';

/**
 * @typedef {Object} HiringRequestFormValues
 * @property {string} designation
 * @property {string} department
 * @property {string} workArrangement
 * @property {string} employmentType
 * @property {string} minAge
 * @property {string} maxAge
 * @property {string} genderPreference
 * @property {string} qualificationPreference
 * @property {string} responsibilities
 * @property {string[]} toolsRequired
 * @property {string} minExperience
 * @property {string} maxExperience
 * @property {string} minCTC
 * @property {string} maxCTC
 * @property {string} noOfPositions
 */
const DEFAULT_VALUES = {
  designation: '',
  department: '',
  workArrangement: '',
  employmentType: '',
  minAge: '',
  maxAge: '',
  genderPreference: '',
  qualificationPreference: '',
  responsibilities: '',
  toolsRequired: [],
  minExperience: '',
  maxExperience: '',
  minCTC: '',
  maxCTC: '',
  noOfPositions: '',
};

/**
 * NOTE:
 * Uses react-hook-form watch() values.
 * Validation depends on render-time field subscription.
 */
const greaterThanRule = (minValue, message) => ({
  validate: validateGreaterThan(minValue, message),
});

const submitHiringRequest =
  ({ mode, posting, onSuccess }) =>
  async (data) => {
    try {
      if (mode === 'edit') {
        if (!posting?.id) return;
        await updateHiringRequest(posting.id, data);
        toast.success('Hiring request updated successfully');
      } else {
        await postHiringRequest(data);
        toast.success('Hiring request submitted successfully');
      }

      onSuccess();
    } catch (error) {
      toast.error(
        logError({
          message: 'Failed to submit hiring request',
          error,
          code: mode === 'edit' ? 'HIRING_UPDATE_FAILED' : 'HIRING_CREATE_FAILED',
        }),
      );
    }
  };

/**
 * HiringRequestForm
 *
 * @param {Object} props
 * @param {'add'|'edit'} [props.mode='add']
 * @param {Object} [props.posting]
 * @param {Function} [props.onCancel]
 */
export default function HiringRequestForm({ mode = HIRING_FORM_MODE.ADD as string, posting, onCancel }: { mode?: string; posting?: any; onCancel?: () => void }) {
  const { onRefresh } = useOutletContext<Record<string, any>>();
  const methods = useForm({ defaultValues: DEFAULT_VALUES });
  const { handleSubmit, reset, watch } = methods;

  const [saving, setSaving] = useState(false);

  // Departments come from the `departments` table rather than a hard-coded list, so the
  // options track whatever Reference Data holds. `job_postings.department` stores the
  // name as text, so the name is both label and value here.
  const fetchDepartments = useCallback(() => departmentsApi.list(), []);
  const { data: departments } = useAsyncData<Department[]>(fetchDepartments, [], {
    code: 'DEPARTMENTS_FOR_HIRING_FAILED',
    errorMessage: 'Failed to load departments',
  });

  const departmentOptions = useMemo(
    () => departments.map((d) => ({ label: d.name, value: d.name })),
    [departments],
  );

  // Work arrangement and employment type are lookup tables as of V7 (they used to be
  // hard-coded here). Both store the label on job_postings, so label and value match.
  const fetchWorkArrangements = useCallback(() => lookupApis.workArrangements.list(), []);
  const { data: workArrangements } = useAsyncData<LookupRow[]>(fetchWorkArrangements, [], {
    code: 'WORK_ARRANGEMENTS_FOR_HIRING_FAILED',
    errorMessage: 'Failed to load work arrangements',
  });

  const fetchEmploymentTypes = useCallback(() => lookupApis.employmentTypes.list(), []);
  const { data: employmentTypes } = useAsyncData<LookupRow[]>(fetchEmploymentTypes, [], {
    code: 'EMPLOYMENT_TYPES_FOR_HIRING_FAILED',
    errorMessage: 'Failed to load employment types',
  });

  // Qualification and tools are lookup tables as of V8 (qualifications was a hard-coded
  // array here, tools had no option list at all — every value was typed by hand). Both
  // store the label on job_postings, so label and value match.
  const fetchQualifications = useCallback(() => lookupApis.qualifications.list(), []);
  const { data: qualifications } = useAsyncData<LookupRow[]>(fetchQualifications, [], {
    code: 'QUALIFICATIONS_FOR_HIRING_FAILED',
    errorMessage: 'Failed to load qualifications',
  });

  const fetchTools = useCallback(() => lookupApis.tools.list(), []);
  const { data: tools } = useAsyncData<LookupRow[]>(fetchTools, [], {
    code: 'TOOLS_FOR_HIRING_FAILED',
    errorMessage: 'Failed to load tools',
  });

  const toLabelOptions = (rows: LookupRow[]) =>
    rows.map((r) => ({ label: r.label ?? r.name ?? r.code, value: r.label ?? r.name ?? r.code }));

  const workArrangementOptions = useMemo(
    () => toLabelOptions(workArrangements),
    [workArrangements],
  );
  const employmentTypeOptions = useMemo(() => toLabelOptions(employmentTypes), [employmentTypes]);
  const qualificationOptions = useMemo(() => toLabelOptions(qualifications), [qualifications]);
  const toolOptions = useMemo(() => toLabelOptions(tools), [tools]);

  const minAge = watch('minAge');
  const minExperience = watch('minExperience');
  const minCTC = watch('minCTC');

  useEffect(() => {
    reset(mode === 'edit' && posting ? { ...DEFAULT_VALUES, ...posting } : DEFAULT_VALUES);
  }, [mode, posting, reset]);

  const handleReset = () => {
    reset(DEFAULT_VALUES);
    onCancel?.();
  };

  const submit = submitHiringRequest({
    mode,
    posting,
    onSuccess: () => {
      handleReset();
      onRefresh?.();
    },
  });

  const onSubmit = async (data) => {
    if (saving) return;

    setSaving(true);
    await submit(data);
    setSaving(false);
  };

  const resolvedFormTitle =
    mode === HIRING_FORM_MODE.ADD ? 'New Hiring Request' : 'Edit Hiring Request';

  return (
    <FormProvider {...methods}>
      <form className='column gap-3' onSubmit={handleSubmit(onSubmit)}>
        <SectionTitle title={resolvedFormTitle} />

        <ControlledTextField
          id='designation'
          name='designation'
          label='Designation'
          rules={{
            required: 'Designation is required',
            minLength: { value: 3, message: 'Minimum 3 characters' },
            maxLength: { value: 50, message: 'Maximum 50 characters' },
          }}
          fullWidth
          labelVariant={LABEL_VARIANT.PRIMARY}
        />

        <ControlledSelect
          id='department'
          name='department'
          label='Department'
          options={departmentOptions}
          rules={{ required: 'Department is required' }}
          fullWidth
          labelVariant={LABEL_VARIANT.PRIMARY}
        />

        <ControlledSelect
          id='workArrangement'
          name='workArrangement'
          label='Work Arrangement'
          options={workArrangementOptions}
          rules={{ required: 'Work Arrangement is required' }}
          fullWidth
          labelVariant={LABEL_VARIANT.PRIMARY}
        />

        <ControlledSelect
          id='employmentType'
          name='employmentType'
          label='Employment Type'
          options={employmentTypeOptions}
          rules={{ required: 'Employment Type is required' }}
          fullWidth
          labelVariant={LABEL_VARIANT.PRIMARY}
        />

        <InlineRangeGroup label='Age group' separator='to'>
          <ControlledTextField
            id='minAge'
            name='minAge'
            label='Minimum Age'
            type={INPUT_TYPES.NUMBER}
            inputMode={INPUT_MODES.NUMERIC}
            rules={{
              required: 'Minimum Age is required',
              min: { value: 18, message: 'Minimum 18 years' },
            }}
            reserveErrorSpace
            orientation={FIELD_ORIENTATION.ROW}
            sx={{ width: 180 }}
            wrapperSx={{ width: 'auto' }}
          />

          <ControlledTextField
            id='maxAge'
            name='maxAge'
            label='Maximum Age'
            type={INPUT_TYPES.NUMBER}
            inputMode={INPUT_MODES.NUMERIC}
            rules={{
              required: 'Maximum Age is required',
              min: { value: 18, message: 'Minimum 18 years' },
              ...greaterThanRule(minAge, 'Maximum age must be greater than minimum age'),
            }}
            reserveErrorSpace
            orientation={FIELD_ORIENTATION.ROW}
            sx={{ width: 180 }}
            wrapperSx={{ width: 'auto' }}
          />
        </InlineRangeGroup>

        <ControlledSelect
          id='genderPreference'
          options={GENDER_PREFERENCES.map((v) => ({ label: v, value: v }))}
          name='genderPreference'
          label='Gender Preferred'
          rules={{ required: 'Gender Preference is required' }}
          fullWidth
          labelVariant={LABEL_VARIANT.PRIMARY}
        />

        <ControlledSelect
          id='qualificationPreference'
          options={qualificationOptions}
          name='qualificationPreference'
          label='Qualification Preferred'
          rules={{ required: 'Qualification Preference is required' }}
          fullWidth
          labelVariant={LABEL_VARIANT.PRIMARY}
        />

        <ControlledTextField
          id='responsibilities'
          name='responsibilities'
          label='Roles and Responsibilities'
          rules={{
            validate: (v) => {
              if (!v?.trim()) return 'Responsibilities are required';
              if (v.trim().length < 3) return 'Minimum 3 characters';
              if (v.trim().length > 1000) return 'Maximum 1000 characters';
              return true;
            },
          }}
          fullWidth
          isMultiline
          rows={5}
          showCharCount
          labelVariant={LABEL_VARIANT.PRIMARY}
        />

        <ControlledComboBox
          id='toolsRequired'
          options={toolOptions}
          name='toolsRequired'
          label='Tools Required'
          rules={{
            validate: (value) => {
              if (!Array.isArray(value) || value.length === 0) {
                return 'At least one tool is required';
              }
              return true;
            },
          }}
          fullWidth
          multiple
          freeSolo
        />

        <InlineRangeGroup label='Experience required' separator='-'>
          <ControlledTextField
            id='minExperience'
            name='minExperience'
            label='Minimum Years'
            type={INPUT_TYPES.NUMBER}
            inputMode={INPUT_MODES.NUMERIC}
            rules={{
              required: 'Minimum Experience is required',
              min: { value: 0, message: 'Minimum 0 years' },
            }}
            orientation={FIELD_ORIENTATION.ROW}
            reserveErrorSpace
            sx={{ width: 180 }}
            wrapperSx={{ width: 'auto' }}
          />

          <ControlledTextField
            id='maxExperience'
            name='maxExperience'
            label='Maximum Years'
            type={INPUT_TYPES.NUMBER}
            inputMode={INPUT_MODES.NUMERIC}
            rules={{
              required: 'Maximum Experience is required',
              min: { value: 0, message: 'Minimum 0 years' },
              ...greaterThanRule(
                minExperience,
                'Maximum experience must be greater than minimum experience',
              ),
            }}
            orientation={FIELD_ORIENTATION.ROW}
            reserveErrorSpace
            sx={{ width: 180 }}
            wrapperSx={{ width: 'auto' }}
          />
        </InlineRangeGroup>

        <InlineRangeGroup label='To offer CTC' separator='-'>
          <ControlledTextField
            id='minCTC'
            name='minCTC'
            label='Minimum amount'
            prefix='₹'
            type={INPUT_TYPES.NUMBER}
            rules={{
              required: 'Minimum CTC is required',
              min: { value: 1, message: 'CTC must be greater than ₹0' },
            }}
            orientation={FIELD_ORIENTATION.ROW}
            reserveErrorSpace
            sx={{ width: 180 }}
            wrapperSx={{ width: 'auto' }}
          />

          <ControlledTextField
            id='maxCTC'
            name='maxCTC'
            label='Maximum amount'
            prefix='₹'
            type={INPUT_TYPES.NUMBER}
            rules={{
              required: 'Maximum CTC is required',
              min: { value: 1, message: 'CTC must be greater than ₹0' },
              ...greaterThanRule(minCTC, 'Maximum CTC must be greater than minimum CTC'),
            }}
            orientation={FIELD_ORIENTATION.ROW}
            reserveErrorSpace
            sx={{ width: 180 }}
            wrapperSx={{ width: 'auto' }}
          />
        </InlineRangeGroup>

        <ControlledSelect
          id='noOfPositions'
          options={POSITION_COUNTS.map((v) => ({ label: v, value: v }))}
          name='noOfPositions'
          label='Number of positions'
          rules={{ required: 'Number of positions is required' }}
          fullWidth
          labelVariant={LABEL_VARIANT.PRIMARY}
        />

        <div className='row self-end'>
          <button
            type='button'
            className='btn cta-outlined-btn'
            disabled={saving}
            onClick={handleReset}
          >
            Cancel
          </button>
          <button type='submit' className='btn cta-btn' disabled={saving}>
            {mode === HIRING_FORM_MODE.EDIT ? 'Update' : 'Post'}
          </button>
        </div>
      </form>
    </FormProvider>
  );
}
